import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Modal = ({ title, description, images = [], isOpen, onClose }) => {
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = () => {
      if (scrollRef.current?.scrollTop > 10) {
        setShowScrollHint(false);
      }
    };

    const scrollContainer = scrollRef.current;

    const checkIfOverflowing = () => {
      if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
        setShowScrollHint(true);
      } else {
        setShowScrollHint(false);
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      scrollContainer?.addEventListener('scroll', handleScroll);
      checkIfOverflowing();
      const timeout = setTimeout(() => setShowScrollHint(false), 4000);

      return () => {
        clearTimeout(timeout);
        window.removeEventListener('keydown', handleKeyDown);
        scrollContainer?.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleImageModalClose = () => {
    setSelectedImage(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Main Modal - Retro Window Style */}
          <motion.div
            className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          >
            <motion.div
              className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_40px_rgba(0,255,255,0.6)] w-full max-w-5xl max-h-[90vh] flex flex-col transition-all duration-500"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Window Title Bar - Retro Style */}
              <div className="bg-gradient-to-r from-blue-400 to-purple-600 dark:from-pink-500 dark:to-purple-600 border-b-4 border-black dark:border-cyan-400 px-6 py-4 flex items-center justify-between transition-all duration-500">
                <div className="flex items-center gap-3">
                  {/* Window Control Dots */}
                  <div className="flex gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                    <div className="w-4 h-4 rounded-full bg-yellow-300 border-2 border-black dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                    <div className="w-4 h-4 rounded-full bg-green-400 border-2 border-black dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight text-black dark:text-white transition-colors duration-500">
                      {title}
                    </h2>
                  </div>
                </div>
                
                {/* Window Controls */}
                <div className="flex gap-2">
                  <button
                    className="w-8 h-8 bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black transition-all dark:text-cyan-300 duration-300"
                    onClick={onClose}
                    aria-label="Minimize"
                  >
                    _
                  </button>
                  <button
                    className="w-8 h-8 bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black transition-all dark:text-cyan-300 duration-300"
                    onClick={onClose}
                    aria-label="Maximize"
                  >
                    □
                  </button>
                  <button
                    className="w-8 h-8 bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-red-500 hover:text-white dark:hover:bg-red-500 transition-all dark:text-cyan-300 duration-300"
                    onClick={onClose}
                    aria-label="Close modal"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div ref={scrollRef} className="relative overflow-y-auto flex-1 bg-white dark:bg-[#1a1a2e] transition-colors duration-500">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col lg:flex-row gap-8">
                    
                    {/* Description Section - Retro Box */}
                    <div className="flex-1 lg:w-1/2">
                      <div className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,0,255,0.5)] transition-all duration-500">
                        <div className="bg-gradient-to-r from-orange-400 to-red-500 dark:from-pink-400 dark:to-purple-500 border-b-4 border-black dark:border-pink-400 px-4 py-2 -mx-6 -mt-6 mb-4 transition-all duration-500">
                          <h3 className="font-black uppercase text-sm tracking-tight text-black dark:text-white transition-colors duration-500">
                            📋 PROJECT OVERVIEW
                          </h3>
                        </div>
                        <div className="space-y-4">
                          {description ? description.split('\n').map((para, index) => (
                            <motion.p 
                              key={index} 
                              className="text-sm md:text-base font-bold leading-relaxed text-gray-800 dark:text-gray-300 transition-colors duration-500"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                              {para.trim()}
                            </motion.p>
                          )) : (
                            <p className="text-sm md:text-base font-bold leading-relaxed text-gray-800 dark:text-gray-300 transition-colors duration-500">
                              No description available.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Images Section - Retro Gallery */}
                    <div className="flex-1 lg:w-1/2">
                      <div className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all duration-500">
                        <div className="bg-gradient-to-r from-green-400 to-emerald-500 dark:from-cyan-400 dark:to-blue-500 border-b-4 border-black dark:border-cyan-400 px-4 py-2 -mx-6 -mt-6 mb-4 transition-all duration-500">
                          <h3 className="font-black uppercase text-sm tracking-tight text-black dark:text-white transition-colors duration-500">
                            🖼️ PROJECT GALLERY
                          </h3>
                        </div>
                        
                        {images.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {images.map((image, index) => (
                              <motion.div
                                key={index}
                                className="group cursor-pointer"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                                onClick={() => handleImageClick(image)}
                              >
                                <div className="relative overflow-hidden border-4 border-black dark:border-yellow-400 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(255,255,0,0.5)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(255,255,0,0.7)] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] duration-500">
                                  <img
                                    src={image.src}
                                    alt={image.alt || `${title} - Image ${index + 1}`}
                                    className="w-full h-48 object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  {/* Fallback */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-pink-400 items-center justify-center text-white dark:text-black hidden transition-all duration-500">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  
                                  {/* Hover Overlay - Retro Style */}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/80 dark:group-hover:bg-cyan-400/90 transition-all duration-300 flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                      <div className="bg-yellow-400 dark:bg-black border-3 border-black dark:border-cyan-400 text-black dark:text-cyan-400 px-4 py-2 font-black uppercase text-xs shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all duration-500">
                                        CLICK TO EXPAND
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {image.caption && (
                                  <p className="text-xs font-bold text-center mt-2 px-2 text-gray-700 dark:text-gray-400 transition-colors duration-500">
                                    {image.caption}
                                  </p>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 transition-all duration-500">
                            <div className="w-16 h-16 bg-gray-300 dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 flex items-center justify-center mb-4 dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all duration-500">
                              <svg className="w-8 h-8 text-gray-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="font-black uppercase text-xs text-gray-600 dark:text-gray-400 transition-colors duration-500">No images available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scroll Hint - Retro Style */}
                {showScrollHint && (
                  <motion.div 
                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-center"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    <div className="bg-black dark:bg-cyan-400 text-white dark:text-black px-4 py-2 border-3 border-black dark:border-cyan-400 font-black uppercase text-xs shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.8)] transition-all duration-500">
                      SCROLL FOR MORE ↓
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>

          {/* Image Expansion Modal - Retro Window */}
          {selectedImage && (
            <motion.div
              className="fixed inset-0 bg-black/90 dark:bg-black/95 flex items-center justify-center z-[60] p-4 transition-all duration-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleImageModalClose}
            >
              <motion.div
                className="relative max-w-6xl max-h-[90vh] w-full"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Image Window */}
                <div className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-yellow-400 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_50px_rgba(255,255,0,0.8)] transition-all duration-500">
                  {/* Image Window Title Bar */}
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 dark:from-yellow-400 dark:to-pink-500 border-b-4 border-black dark:border-yellow-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-black dark:bg-yellow-400 dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                      <span className="font-black uppercase text-sm tracking-tight text-black">IMAGE_VIEWER.exe</span>
                    </div>
                    <button
                      onClick={handleImageModalClose}
                      className="w-8 h-8 bg-white dark:bg-black border-3 border-black dark:border-yellow-400 flex items-center justify-center font-black text-xs hover:bg-red-500 hover:text-white dark:hover:bg-red-500 transition-all dark:text-yellow-400 duration-300"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Image Content */}
                  <div className="p-4 bg-white dark:bg-[#1a1a2e] transition-colors duration-500">
                    <div className="border-4 border-black dark:border-yellow-400 overflow-hidden transition-all duration-500">
                      <img
                        src={selectedImage.src}
                        alt={selectedImage.alt}
                        className="w-full h-full max-h-[70vh] object-contain bg-gray-100 dark:bg-[#2d2d44] transition-colors duration-500"
                      />
                    </div>
                    
                    {/* Caption */}
                    {selectedImage.caption && (
                      <div className="mt-4 bg-yellow-400 dark:bg-cyan-400 border-4 border-black dark:border-cyan-400 p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.6)] transition-all duration-500">
                        <p className="text-center font-black uppercase text-sm text-black">{selectedImage.caption}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;